import { ReactElement, useCallback, useMemo } from 'react';

import { DefaultComponentProperty } from '@braskai/utils';
import { useBeforeUnload } from 'react-router-dom';

import { useOnboardingStore } from '@/entities/user/currentUser';

import { OnboardingStepKeys } from '@/shared/types/Onboarding';

import { getOnboardingSteps, stepsWithNextStep } from '../../lib';
import { useUpdateOnboardingSteps } from '../../model/useUpdateOnboardingSteps';
import { OnboardingPopper } from './OnboardingPopper';
import { STooltip } from './styled';

type OnboardingTooltipProps = {
  children: ReactElement;
  open?: boolean;
  stepName: OnboardingStepKeys;
} & DefaultComponentProperty;

export const OnboardingTooltip = ({ children, open = true, stepName }: OnboardingTooltipProps) => {
  const isCurrentStep = useOnboardingStore((state) => state.currentStep === stepName);
  const setNextStep = useOnboardingStore((state) => state.setNextStep);

  const onboardingSteps = getOnboardingSteps();

  const stepData = (isCurrentStep && onboardingSteps[stepName]) || null;
  const { placement } = stepData || {};
  const isOpen = !!stepData && open;

  const updateOnboardingSteps = useUpdateOnboardingSteps();

  const handleCloseOnboarding = useCallback(() => {
    if (isOpen) {
      updateOnboardingSteps();
    }
  }, [isOpen, updateOnboardingSteps]);

  const handleCloseStep = useCallback(() => {
    if (isOpen) {
      updateOnboardingSteps();

      if (stepsWithNextStep.has(stepName)) {
        setNextStep();
      }
    }
  }, [isOpen, updateOnboardingSteps, stepName, setNextStep]);

  const popper = useMemo(
    () => (isOpen ? <OnboardingPopper step={stepName} onClose={handleCloseStep} /> : null),
    [isOpen, stepName, handleCloseStep],
  );

  useBeforeUnload(handleCloseOnboarding);

  return (
    <STooltip
      title={popper}
      hasHighlight={stepData?.hasHighlight}
      open={isOpen}
      placement={placement}
      arrow
      disableFocusListener
      disableHoverListener
    >
      {children}
    </STooltip>
  );
};
